import { useState, useEffect } from 'react';

/**
 * True when this environment can evaluate a media query at all.
 *
 * `window.matchMedia` is guarded rather than assumed. It is absent in jsdom and in any renderer
 * without a layout engine, and an unguarded call throws there — which takes down every component
 * underneath a hook that only wanted to know how wide the screen is. Absent means "no match": the
 * wide layout, which is the one that holds every column.
 */
function canMeasure(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function';
}

export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => (canMeasure() ? window.matchMedia(query).matches : false));

  useEffect(() => {
    if (!canMeasure()) return;
    const mediaQuery = window.matchMedia(query);
    const handler = (event: MediaQueryListEvent) => setMatches(event.matches);

    setMatches(mediaQuery.matches);
    mediaQuery.addEventListener('change', handler);

    return () => mediaQuery.removeEventListener('change', handler);
  }, [query]);

  return matches;
}

export function useIsDesktop() {
  return useMediaQuery('(min-width: 1280px)');
}

export function useIsTablet() {
  return useMediaQuery('(min-width: 768px) and (max-width: 1279px)');
}

export function useIsMobile() {
  return useMediaQuery('(max-width: 767px)');
}
